// config/constants.ts
// Application-wide constants with TypeScript types

// App information
export const APP_NAME = 'RoundMaster' as const;
export const APP_VERSION = '2.0.0' as const;

// Timer defaults
export const TIMER = {
  DEFAULT_DURATION: 50 * 60, // 50 minutes in seconds
  DEFAULT_COLOR: '#00FFD4',
  WARNING_THRESHOLD: 5 * 60, // 5 minutes
  CRITICAL_THRESHOLD: 1 * 60, // 1 minute
} as const;

// Roles
export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'Manager',
  HEAD_JUDGE: 'Head Judge',
  SCOREKEEPER: 'Scorekeeper',
  SHIELD: 'Shield',
  TEAM_LEAD: 'Team Lead',
  FLOOR_JUDGE: 'Floor Judge',
  FEATURE_MATCH_JUDGE: 'Feature Match',
  STREAM: 'Stream',
  PENDING: 'pending',
} as const;

export type RoleKey = keyof typeof ROLES;
export type RoleValue = (typeof ROLES)[RoleKey];

// Role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY: Record<RoleValue, number> = {
  [ROLES.ADMIN]: 100,
  [ROLES.MANAGER]: 90,
  [ROLES.HEAD_JUDGE]: 70,
  [ROLES.SCOREKEEPER]: 60,
  [ROLES.SHIELD]: 50,
  [ROLES.TEAM_LEAD]: 40,
  [ROLES.FLOOR_JUDGE]: 30,
  [ROLES.FEATURE_MATCH_JUDGE]: 25,
  [ROLES.STREAM]: 20,
  [ROLES.PENDING]: 0,
};

// Table statuses
export const TABLE_STATUS = {
  PLAYING: 'playing',
  FINISHED: 'finished',
  DRAW: 'draw',
  ONE_NO_SHOW: 'oneNoShow',
  BOTH_NO_SHOW: 'bothNoShow',
  APPEAL_OPEN: 'appealOpen',
  APPEAL_PROGRESS: 'appealProgress',
  DROP_OUT: 'dropOut',
  CALL_IN_PROGRESS: 'callInProgress',
  STILL_PLAYING: 'stillPlaying',
  NO_PLAYERS: 'noPlayers',
  MISSING: 'missing',
  FEATURED: 'featured',
  HAS_COMMENT: 'hasComment',
  STATIC_CONFLICT: 'staticConflict',
} as const;

export type TableStatusKey = keyof typeof TABLE_STATUS;
export type TableStatusValue = (typeof TABLE_STATUS)[TableStatusKey];

// Appeal statuses
export const APPEAL_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in progress',
  CONCLUDED: 'concluded',
} as const;

export type AppealStatusValue = (typeof APPEAL_STATUS)[keyof typeof APPEAL_STATUS];

// Appeal judgements
export const APPEAL_JUDGEMENT = {
  INITIAL_CORRECT: 'initial_correct',
  OVERTURNED: 'overturned',
  OTHER: 'other',
} as const;

export type AppealJudgementValue = (typeof APPEAL_JUDGEMENT)[keyof typeof APPEAL_JUDGEMENT];

// Penalty types
export const PENALTY_TYPES = [
  'Warning',
  'Game Loss',
  'Match Loss',
  'Disqualification',
] as const;

export type PenaltyType = (typeof PENALTY_TYPES)[number];

// Penalty infractions (common ones)
export const PENALTY_INFRACTIONS = [
  'Tardiness',
  'Deck Problem',
  'Drawing Extra Cards',
  'Looking at Extra Cards',
  'Slow Play',
  'Unsporting Conduct - Minor',
  'Unsporting Conduct - Major',
  'Outside Assistance',
  'Improperly Determining a Winner',
  'Other',
] as const;

export type PenaltyInfraction = (typeof PENALTY_INFRACTIONS)[number];

// Match results
export const MATCH_RESULTS = {
  WIN: 'win',
  LOSS: 'loss',
  DRAW: 'draw',
  NO_SHOW: 'noShow',
  DROP: 'drop',
} as const;

export type MatchResultValue = (typeof MATCH_RESULTS)[keyof typeof MATCH_RESULTS];

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

// File upload
export const FILE_UPLOAD = {
  MAX_SIZE_MB: 5,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const,
  ALLOWED_CSV_TYPES: ['text/csv', 'application/vnd.ms-excel'] as const,
} as const;

// Debounce/Throttle times (ms)
export const TIMING = {
  DEBOUNCE_SEARCH: 300,
  DEBOUNCE_INPUT: 150,
  THROTTLE_SCROLL: 100,
  AUTO_SAVE_DELAY: 2000,
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  THEME_PREFERENCE: 'roundmaster_theme',
  LAST_TOURNAMENT: 'roundmaster_last_tournament',
  TABLE_VIEW_PREFERENCE: 'roundmaster_table_view',
} as const;

// Date/Time formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  DISPLAY_WITH_TIME: 'MMM d, yyyy h:mm a',
  TIME_ONLY: 'h:mm a',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
} as const;

// API/Firebase paths
export const DB_PATHS = {
  USERS: 'users',
  USERNAMES: 'usernames',
  TOURNAMENTS: 'tournaments',
  APPEALS_LOG: 'appealsLog',
  FEATURE_REQUESTS: 'featureRequests',
  BUG_REPORTS: 'bugReports',
  IMPLEMENTED_FEATURES: 'implementedFeatures',
  FIXED_BUGS: 'fixedBugs',
  STREAM_SESSIONS: 'streamSessions',
} as const;

// Account Types
export const ACCOUNT_TYPES = {
  PLAYER: 'player',
  PARENT: 'parent',
  CHILD: 'child',
  STAFF: 'staff',
} as const;

export type AccountType = (typeof ACCOUNT_TYPES)[keyof typeof ACCOUNT_TYPES];

// Child account email domain (internal use only - not a real domain)
export const CHILD_EMAIL_DOMAIN = 'child.roundmaster.local' as const;

// Reserved usernames that cannot be used for child accounts
export const RESERVED_USERNAMES = [
  'admin', 'administrator', 'support', 'help', 'system',
  'roundmaster', 'root', 'mod', 'moderator', 'staff',
  'tournament', 'player', 'parent', 'child', 'guest',
  'judge', 'scorekeeper', 'manager', 'official',
] as const;

// Username validation rules
export const USERNAME_RULES = {
  MIN_LENGTH: 3,
  MAX_LENGTH: 20,
  PATTERN: /^[a-z][a-z0-9_]{2,19}$/,
} as const;

const constants = {
  APP_NAME,
  APP_VERSION,
  TIMER,
  ROLES,
  ROLE_HIERARCHY,
  TABLE_STATUS,
  APPEAL_STATUS,
  APPEAL_JUDGEMENT,
  PENALTY_TYPES,
  PENALTY_INFRACTIONS,
  MATCH_RESULTS,
  PAGINATION,
  FILE_UPLOAD,
  TIMING,
  STORAGE_KEYS,
  DATE_FORMATS,
  DB_PATHS,
  ACCOUNT_TYPES,
  CHILD_EMAIL_DOMAIN,
  RESERVED_USERNAMES,
  USERNAME_RULES,
};

export default constants;
